import { Box } from '@mui/material';
import { useState, Suspense } from 'react';
import { DrawerHeader, PinDrawer } from '@components/drawerItem/DrawerHeader';
import { DetailGroup } from '@pages/QuoteDetails/sidebarItems/summary/DetailGroup';
import { DetailItem, DetailItemProps } from '@pages/QuoteDetails/sidebarItems/summary/DetailItem';
import { SummaryTab, SummaryTabs } from '@pages/QuoteDetails/sidebarItems/summary/SummaryTabs';
import { QuoteType } from '@models/quotes';
import Loading from '@components/Loading';
import { QuoteActivity } from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/QuoteActivity';
import {
  RatingSummaryModal,
  TotalInsurableValueModal,
  KYTaxSurchargeModal,
} from '@pages/QuoteDetails/sidebarItems/summary/modals';
import { ModalType } from './getQuoteSummaryData';
import { useQuoteSummaryHelper } from './useQuoteSummaryHelper';
import { scrollbarStyle } from '@theme/styles';
import { QuoteDrawerUrlParams } from '@pages/QuoteDetails/constants/drawerListItems';

export const SEARCH_PARAMS = {
  [ModalType.RatingSummaryModal]: 'ratingDetails',
  [ModalType.TIVModal]: 'TIVDetails',
  [ModalType.KYTaxModal]: 'kyTaxDetails',
} as const;

export const Summary = () => {
  const [drawerTab, setDrawerTab] = useState(SummaryTab.Details);

  const {
    quoteId,
    quote,
    structuredQuote,
    lastSuccessfulRating,
    isRatingModalOpen,
    isTIVModalOpen,
    isKYTaxModalOpen,
    setModalOpen,
    summaryData,
  } = useQuoteSummaryHelper();

  const summaryDetails = [
    summaryData.quoteDetails,
    summaryData.customerDetails,
    summaryData.partnerAgent,
    summaryData.customerService,
  ];

  const quoteType = quote?.quoteType as QuoteType;

  if (!structuredQuote || !quote) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Loading />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', mr: '4px' }}>
      <DrawerHeader drawerTitle='Summary' padding='19px 16px 0'>
        <SummaryTabs activeTab={drawerTab} onActiveTabChange={setDrawerTab} quote={quote} />
        <PinDrawer urlParam={QuoteDrawerUrlParams.Summary} />
      </DrawerHeader>
      <Box sx={{ overflowY: 'auto', flexGrow: 1, ...scrollbarStyle }}>
        <Box sx={{ paddingX: 2 }}>
          {drawerTab === SummaryTab.Details && (
            <>
              {summaryDetails.map((group) => (
                <DetailGroup name={group.label} key={group.label}>
                  {group.fields.map((field: DetailItemProps) => (
                    <DetailItem label={field.label} value={field.value} suffix={field.suffix} key={field.label} />
                  ))}
                </DetailGroup>
              ))}
              <Suspense fallback={<Box sx={{ p: 2 }}>Loading details...</Box>}>
                {lastSuccessfulRating && isRatingModalOpen && (
                  <RatingSummaryModal
                    quoteId={quoteId}
                    isOpen={isRatingModalOpen}
                    onCloseModal={() => setModalOpen(ModalType.RatingSummaryModal, false)}
                  />
                )}
                {isTIVModalOpen && (
                  <TotalInsurableValueModal
                    quoteId={quoteId}
                    isOpen={isTIVModalOpen}
                    policyNumber={quote?.policyNumber?.policyNumber}
                    onCloseModal={() => setModalOpen(ModalType.TIVModal, false)}
                  />
                )}
                {isKYTaxModalOpen && (
                  <KYTaxSurchargeModal
                    quoteId={quoteId}
                    isOpen={isKYTaxModalOpen}
                    onCloseModal={() => setModalOpen(ModalType.KYTaxModal, false)}
                  />
                )}
              </Suspense>
            </>
          )}
        </Box>
        {drawerTab === SummaryTab.Activity && <QuoteActivity quoteId={quoteId} quoteType={quoteType} />}
      </Box>
    </Box>
  );
};
